import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { discover, recipes as recipesApi } from '../lib/api';

function Discover() {
  const [categories, setCategories] = useState([]);
  const [cuisines, setCuisines] = useState([]);
  const [dietary, setDietary] = useState([]);
  const [creators, setCreators] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [categoriesData, cuisinesData, dietaryData, creatorsData] = await Promise.all([
        discover.getCategories(),
        discover.getCuisines(),
        discover.getDietary(),
        discover.getCreators(),
      ]);

      setCategories(categoriesData.categories || []);
      setCuisines(cuisinesData.cuisines || []);
      setDietary(dietaryData.dietary || []);
      setCreators(creatorsData.creators || []);
    } catch (err) {
      setError(err.message);
    }

    setIsLoading(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-4">{error}</div>
        <button onClick={loadData} className="btn btn-secondary">Try Again</button>
      </div>
    );
  }

  const isEmpty = categories.length === 0 && cuisines.length === 0 && creators.length === 0;

  if (isEmpty) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Recipes Yet</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-6">Save some recipes to see them organized by category, cuisine, and creator.</p>
        <Link to="/new" className="btn btn-primary">Add Your First Recipe</Link>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Discover</h1>
        <p className="text-gray-500 dark:text-gray-400">Browse your recipes by category, cuisine, and creator</p>
      </div>

      {/* Creators Section */}
      {creators.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Your Creators</h2>
            <Link to="/discover/creators" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
              View All
            </Link>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {creators.slice(0, 10).map((creator) => (
              <Link
                key={creator.name}
                to={`/discover/creator/${encodeURIComponent(creator.name)}`}
                className="flex-shrink-0 text-center"
              >
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-white text-xl font-semibold mb-2">
                  {creator.sampleThumbnail ? (
                    <img src={creator.sampleThumbnail} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    creator.name.replace('@', '').charAt(0).toUpperCase()
                  )}
                </div>
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate w-20">{creator.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{creator.recipeCount} recipes</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Categories Section */}
      {categories.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Categories</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {categories.slice(0, 8).map((cat) => (
              <Link
                key={cat.name}
                to={`/discover/category/${encodeURIComponent(cat.name)}`}
                className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
              >
                <span className="text-2xl">{cat.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white truncate capitalize">
                    {cat.name.replace(/-/g, ' ')}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{cat.count} recipes</p>
                </div>
              </Link>
            ))}
          </div>
          {categories.length > 8 && (
            <Link to="/discover/categories" className="block text-center text-primary-600 hover:text-primary-700 mt-4 text-sm font-medium">
              View all {categories.length} categories
            </Link>
          )}
        </section>
      )}

      {/* Cuisines Section */}
      {cuisines.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Cuisines</h2>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {cuisines.map((cuisine) => (
              <Link
                key={cuisine.name}
                to={`/discover/cuisine/${encodeURIComponent(cuisine.name)}`}
                className="flex-shrink-0 flex flex-col items-center gap-1 px-4 py-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
              >
                <span className="text-2xl">{cuisine.icon}</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">{cuisine.name}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">{cuisine.count}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Dietary Section */}
      {dietary.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Dietary</h2>
          <div className="flex flex-wrap gap-2">
            {dietary.map((diet) => (
              <Link
                key={diet.name}
                to={`/discover/dietary/${encodeURIComponent(diet.name)}`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 rounded-full border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
              >
                <span>{diet.icon}</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">{diet.name.replace(/-/g, ' ')}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">({diet.count})</span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// Category/Cuisine/Dietary recipes list view
export function DiscoverRecipesList() {
  const { type, value } = useParams();
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [icon, setIcon] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadRecipes();
  }, [type, value]);

  const loadRecipes = async () => {
    setIsLoading(true);
    setError(null);

    try {
      let data;
      if (type === 'category') {
        data = await discover.getByCategory(value);
      } else if (type === 'cuisine') {
        data = await discover.getByCuisine(value);
      } else if (type === 'dietary') {
        data = await discover.getByDietary(value);
      }

      setRecipes(data.recipes || []);
      setPagination(data.pagination);
      setIcon(data.icon || '');
    } catch (err) {
      setError(err.message);
    }

    setIsLoading(false);
  };

  const title = value?.replace(/-/g, ' ');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-4">{error}</div>
        <button onClick={loadRecipes} className="btn btn-secondary">Try Again</button>
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => navigate('/discover')} className="btn-icon mb-4">
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        <span className="ml-2">Back to Discover</span>
      </button>

      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 capitalize">
        {icon} {title}
      </h1>

      {recipes.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">No recipes found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {recipes.map((recipe) => (
            <RecipeCard key={recipe.id} recipe={recipe} />
          ))}
        </div>
      )}
    </div>
  );
}

// Creator detail view
export function CreatorDetail() {
  const { creatorName } = useParams();
  const navigate = useNavigate();
  const [savedRecipes, setSavedRecipes] = useState([]);
  const [discoverContent, setDiscoverContent] = useState([]);
  const [usage, setUsage] = useState(null);
  const [isLoadingSaved, setIsLoadingSaved] = useState(true);
  const [isLoadingDiscover, setIsLoadingDiscover] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, [creatorName]);

  const loadData = async () => {
    setIsLoadingSaved(true);
    setIsLoadingDiscover(true);

    // Load saved recipes
    try {
      const data = await recipesApi.getByCreator(creatorName);
      setSavedRecipes(data.recipes || []);
    } catch (err) {
      console.error('Error loading saved recipes:', err);
    }
    setIsLoadingSaved(false);

    // Load discover content
    try {
      const data = await discover.getCreatorContent(creatorName);
      setDiscoverContent(data.discoverContent || []);
      setUsage(data.usage);
    } catch (err) {
      console.error('Error loading discover content:', err);
    }
    setIsLoadingDiscover(false);
  };

  const handleFetchContent = async () => {
    setIsFetching(true);
    setError(null);

    try {
      await discover.fetchCreatorContent(creatorName);
      // Reload discover content
      const data = await discover.getCreatorContent(creatorName);
      setDiscoverContent(data.discoverContent || []);
      setUsage(data.usage);
    } catch (err) {
      setError(err.message);
    }

    setIsFetching(false);
  };

  return (
    <div>
      <button onClick={() => navigate('/discover')} className="btn-icon mb-4">
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        <span className="ml-2">Back to Discover</span>
      </button>

      {/* Creator Header */}
      <div className="flex items-center gap-4 mb-8 p-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-white text-2xl font-bold">
          {decodeURIComponent(creatorName).replace('@', '').charAt(0).toUpperCase()}
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{decodeURIComponent(creatorName)}</h1>
          <p className="text-gray-500 dark:text-gray-400">{savedRecipes.length} saved recipes</p>
        </div>
      </div>

      {/* Saved Recipes */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Saved Recipes</h2>
        {isLoadingSaved ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : savedRecipes.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400">No saved recipes from this creator</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {savedRecipes.slice(0, 6).map((recipe) => (
              <RecipeCard key={recipe.id} recipe={recipe} />
            ))}
          </div>
        )}
      </section>

      {/* Discover More */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Discover More</h2>
          {usage && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {usage.discoveriesRemaining}/{usage.limit} discoveries left this month
            </span>
          )}
        </div>

        {isLoadingDiscover ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : discoverContent.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
            <p className="text-gray-500 dark:text-gray-400 mb-4">Fetch new content from {decodeURIComponent(creatorName)}</p>
            <button
              onClick={handleFetchContent}
              disabled={isFetching || (usage && usage.discoveriesRemaining === 0)}
              className="btn btn-primary"
            >
              {isFetching ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Fetching...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                  </svg>
                  Discover New Content
                </>
              )}
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
              {discoverContent.map((content) => (
                <a
                  key={content.id}
                  href={content.postUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
                >
                  {content.thumbnailUrl ? (
                    <img src={content.thumbnailUrl} alt="" className="w-full h-32 object-cover" />
                  ) : (
                    <div className="w-full h-32 bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                      <svg className="w-8 h-8 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                    </div>
                  )}
                  <div className="p-3">
                    {content.isRecipeLikely && (
                      <span className="inline-flex items-center gap-1 text-xs text-accent-600 dark:text-accent-400 mb-1">
                        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                        </svg>
                        Likely Recipe
                      </span>
                    )}
                    {content.caption && (
                      <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">{content.caption}</p>
                    )}
                    {content.likesCount && (
                      <p className="text-xs text-gray-400 mt-1">{formatNumber(content.likesCount)} likes</p>
                    )}
                  </div>
                </a>
              ))}
            </div>
            <button
              onClick={handleFetchContent}
              disabled={isFetching || (usage && usage.discoveriesRemaining === 0)}
              className="btn btn-secondary w-full"
            >
              Refresh Content
            </button>
          </>
        )}

        {error && (
          <div className="mt-4 text-red-500 text-sm">{error}</div>
        )}
      </section>
    </div>
  );
}

// All creators list
export function AllCreators() {
  const navigate = useNavigate();
  const [creators, setCreators] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadCreators();
  }, []);

  const loadCreators = async () => {
    try {
      const data = await discover.getCreators();
      setCreators(data.creators || []);
    } catch (err) {
      console.error('Error loading creators:', err);
    }
    setIsLoading(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => navigate('/discover')} className="btn-icon mb-4">
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        <span className="ml-2">Back to Discover</span>
      </button>

      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">All Creators</h1>

      <div className="space-y-3">
        {creators.map((creator) => (
          <Link
            key={creator.name}
            to={`/discover/creator/${encodeURIComponent(creator.name)}`}
            className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
          >
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-white font-semibold">
              {creator.sampleThumbnail ? (
                <img src={creator.sampleThumbnail} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                creator.name.replace('@', '').charAt(0).toUpperCase()
              )}
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900 dark:text-white">{creator.name}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{creator.recipeCount} recipes</p>
            </div>
            <svg className="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </Link>
        ))}
      </div>
    </div>
  );
}

// All categories list
export function AllCategories() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const data = await discover.getCategories();
      setCategories(data.categories || []);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
    setIsLoading(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => navigate('/discover')} className="btn-icon mb-4">
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        <span className="ml-2">Back to Discover</span>
      </button>

      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">All Categories</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {categories.map((cat) => (
          <Link
            key={cat.name}
            to={`/discover/category/${encodeURIComponent(cat.name)}`}
            className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
          >
            <span className="text-2xl">{cat.icon}</span>
            <div className="flex-1">
              <p className="font-medium text-gray-900 dark:text-white capitalize">{cat.name.replace(/-/g, ' ')}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{cat.count} recipes</p>
            </div>
            <svg className="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </Link>
        ))}
      </div>
    </div>
  );
}

// Recipe card component
function RecipeCard({ recipe }) {
  return (
    <Link
      to={`/recipe/${recipe.id}`}
      className="block bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
    >
      {recipe.thumbnailUrl ? (
        <img src={recipe.thumbnailUrl} alt="" className="w-full h-40 object-cover" />
      ) : (
        <div className="w-full h-40 bg-gradient-to-br from-primary-100 to-accent-100 dark:from-primary-900/30 dark:to-accent-900/30 flex items-center justify-center">
          <svg className="w-12 h-12 text-primary-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
          </svg>
        </div>
      )}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 dark:text-white line-clamp-2">{recipe.title}</h3>
        {recipe.totalTime && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            {recipe.totalTime}
          </p>
        )}
        {recipe.isFavorite && (
          <span className="inline-flex items-center gap-1 text-yellow-500 text-sm mt-2">
            <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
            Favorite
          </span>
        )}
      </div>
    </Link>
  );
}

function formatNumber(num) {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
}

export default Discover;
